import { css, StyleSheet } from 'aphrodite'
import * as React from 'react'

import { UnitAction } from '../../engine/actions/action'
import { ICell, Terrain } from '../../engine/map'
import Unit, { UnitStatus } from '../../engine/unit'
import { ITrait } from '../../engine/units/traits'
import ActionGlyph from '../components/actionGlyph'
import Layout from '../components/layout'
import TraitGlyph from '../components/traitGlyph'
import UnitGlyph from '../components/unitGlyph'
import style from '../utils/style'
import Store from './store'

const styles = StyleSheet.create({
  main: {
    borderLeft: style.border,
    width: 360,
    maxWidth: '42vw',
    minWidth: 240,
    minHeight: 0,
    flex: '0 1 360px',
    overflow: 'hidden',

    '@media (max-width: 720px)': {
      borderLeft: 0,
      borderTop: style.border,
      width: '100%',
      maxWidth: 'none',
      minWidth: 0,
      flex: '0 0 34vh',
    },
  },
  container: {
    padding: 10,
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'auto',
  },
  button: {
    padding: 2,
    cursor: 'pointer',
    border: '5px solid transparent',
  },
  subtitle: {
    fontSize: 'smaller',
  },
  disabledAction: {
    cursor: 'default',
    color: style.grey,
  },
  disabledActionIcon: {
    fill: style.grey,
  },
  selectedAction: {
    borderColor: style.gold,
  },
  endTurnButton: {
    flex: '0 0 auto',
    padding: 20,
    textAlign: 'center',
    background: style.darkGrey,
    backgroundClip: 'padding-box',
    borderTop: style.border,
    cursor: 'pointer',
    boxShadow: 'inset 0 0 0 999px rgba(133, 113, 0, .12)',
    ':hover': {
      background: style.gold,
      backgroundClip: 'padding-box',
      color: style.darkGrey,
      boxShadow: 'none',
    },
  },
  disabledEndTurnButton: {
    cursor: 'default',
    color: style.grey,
    boxShadow: 'none',
    ':hover': {
      background: style.darkGrey,
      backgroundClip: 'padding-box',
      color: style.grey,
    },
  },
  unitIcon: {
    width: '30%',
    height: '1%',
  },
  hpBar: { color: style.hpColor },
  mpBar: { color: style.mpColor },
  manaBar: { color: style.manaColor },
})

function renderUnitButton(title, subtitle, icon, onClick?, selected?) {
  const classes = [
    styles.button,
    selected && styles.selectedAction,
    !onClick && styles.disabledAction,
  ]
  icon = React.cloneElement(icon, {
    classes: !onClick && styles.disabledActionIcon,
  })

  return (
    <Layout classes={classes} direction="row" align="center" onClick={onClick}>
      {icon}
      <Layout grow>
        {title}
        <div className={css(styles.subtitle)}>
          {subtitle}
        </div>
      </Layout>
    </Layout>
  )
}

function renderActionButton(
  action: UnitAction, selectedAction: UnitAction | undefined, store: Store,
) {
  const { selection, playerFaction } = store.state
  const selectedUnit = selection && selection.unit && selection.unit.unit
  const previewOnly = !!(selectedUnit
    && selectedUnit.factionId !== playerFaction)
  let manaCost
  if (action.manaCost) {
    manaCost = (
      <span className={css(styles.manaBar)}>
        ({action.manaCost} mana)
      </span>
    )
  }

  const title = <span>{action.name} {manaCost}</span>
  const subtitle = action.description
  const icon = <ActionGlyph action={action} wrapped={true} />
  const onClick = (action.canExecute || previewOnly)
    && (() => store.selectAction(action))
  const selected = action === selectedAction

  return renderUnitButton(title, subtitle, icon, onClick, selected)
}

function renderTraitButton(trait: ITrait) {
  const title = <span>{trait.name} (passive)</span>
  const subtitle = trait.description
  const icon = <TraitGlyph trait={trait} wrapped={true} />

  return renderUnitButton(title, subtitle, icon)
}

function renderCellInfo(cell: ICell) {
  return (
    <p>
      {cell.pos.toString()} - terrain type: {Terrain[cell.terrain]}
    </p>
  )
}

function statusTurnsRemaining(expiration: number) {
  return Math.max(1, expiration)
}

function renderStatusText(status: UnitStatus, expiration: number) {
  const turns = statusTurnsRemaining(expiration)
  const turnLabel = turns === 1 ? 'turn' : 'turns'
  return `${UnitStatus[status]} (${turns} ${turnLabel})`
}

function shouldDisplayStatus(unit: Unit, status: UnitStatus, expiration: number) {
  return expiration > 0
    || status === UnitStatus.Guard
    || unit.factionId === unit.game.currenFaction.id
}

function renderUnitInfo(unit: Unit) {
  const statuses = Array.from(unit.status.entries())
    .filter(([status, expiration]) =>
      shouldDisplayStatus(unit, status, expiration),
    )
    .map(([status, expiration]) => renderStatusText(status, expiration))

  return (
    <div>
      <Layout direction="row" >
        <UnitGlyph
          unitType={unit.type}
          classes={styles.unitIcon}
          wrapped={true}
        />
        <Layout>
          <div>
            {unit.type.name}            
          </div>
          <div>
            Status: {statuses.join(', ') || 'none'}
          </div>
          <div>
            resistance: {unit.resistance}
          </div>
          <div className={css(styles.hpBar)}>
            hp: {unit.hp}/{unit.type.hp}
          </div>
          <div className={css(styles.mpBar)}>
            mp: {unit.mp}/{unit.type.mp}
          </div>
          <div className={css(styles.manaBar)}>
            mana: {unit.mana}/{unit.type.mana}
          </div>
        </Layout>
      </Layout>
    </div>
  )
}

function renderActions(
  unit: Unit, selectedAction: UnitAction | undefined, store: Store,
) {
  return (
    <div>
      {unit.actions.map(a => renderActionButton(a, selectedAction, store))}
    </div>
  )
}

function renderTraits(unit: Unit) {
  if (!unit.type.traits) {
    return null
  }

  return (
    <div>
      {unit.type.traits.map(t => renderTraitButton(t))}
    </div>
  )
}

interface IProps {
  store: Store,
}

export default function Sidebar({ store }: IProps) {
  const { game, hover, playerFaction, selection, turnBanner } = store.state
  const { currenFaction, epoch } = game
  const canEndTurn = !turnBanner && currenFaction.id === playerFaction
  const unit =  (hover && hover.unit) || (selection && selection.unit)
  const cell =  (hover && hover.cell) || (selection && selection.cell)
  const action = hover ? undefined : selection && selection.unit
    && selection.unit.action && selection.unit.action.action

  const cellInfo = cell && renderCellInfo(cell)
  const unitInfo = unit && renderUnitInfo(unit.unit)
  const actionsInfo = unit && renderActions(unit.unit, action, store)
  const traitsInfo = unit && renderTraits(unit.unit)

  return (
    <Layout classes={[styles.main]}>
      <div className={css(styles.container)}>
        <h2>Turn: {epoch} ({currenFaction.name})</h2>
        {cellInfo}
        {unitInfo}
        {actionsInfo}
        {traitsInfo}
      </div>
      <div
        className={css(
          styles.endTurnButton,
          !canEndTurn && styles.disabledEndTurnButton,
        )}
        onClick={canEndTurn ? () => store.endTurn() : undefined}
      >
        End Turn
      </div>
    </Layout>
  )
}



// WEBPACK FOOTER //
// ./src/ui/stageView/sidebar.tsx